import * as React from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import Sitemark from './SitemarkIcon';
import type {} from '@mui/material/themeCssVarsAugmentation';
import Link from '@mui/material/Link';
import { keyframes, useTheme } from '@emotion/react';

import { useNavigate } from 'react-router-dom';
import { AuthData } from '../../../pages/Auth/reducer';
import { HandymanAppState } from '../../../configuration/redux/reducers';
import { connect, useSelector } from 'react-redux';
import { getCurrentUserDetails, logout } from '../../../pages/Auth/actions';
import {
  alpha,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Menu,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { UserDetailsViewModel } from '../../../configuration/api/apiSchema';
import { getAuthData } from '../../../helpers/localStorageHelpers';
import Badge from '@mui/material/Badge';
import { api } from '../../../configuration/api/apiHelper';
import { MessageViewModel } from '../../../configuration/api/apiSchema';
import BuildIcon from '@mui/icons-material/Build';
import WorkIcon from '@mui/icons-material/Work';
import { useColorScheme } from '@mui/material/styles';
import {
  GlassLogo,
  GlassHeaderContainer,
  GlassShadows,
  GlassColors,
  GlassButton,
} from '../../glassmorphism/GlassmorphismStyles';

// Nav Icons
import { ReactComponent as HomeIcon } from '../../../assets/icons/nav/home.svg';
import { ReactComponent as FindWorkIcon } from '../../../assets/icons/nav/findwork.svg';
import { ReactComponent as SuitcaseIcon } from '../../../assets/icons/nav/suitcase.svg';
import { ReactComponent as MyTasksIcon } from '../../../assets/icons/nav/mytasks.svg';
import { ReactComponent as PortfolioIcon } from '../../../assets/icons/nav/porf.svg';
import { ReactComponent as LogoutIcon } from '../../../assets/icons/nav/logout.svg';
import { ReactComponent as BrowseProfIcon } from '../../../assets/icons/nav/browseprof.svg';
import { ReactComponent as CreateOrderIcon } from '../../../assets/icons/nav/creare_order.svg';
import { ReactComponent as FindProIcon } from '../../../assets/icons/nav/find_pro.svg';
import { ReactComponent as BecomeTaskerIcon } from '../../../assets/icons/nav/become_tasker.svg';
import { ReactComponent as HowToIcon } from '../../../assets/icons/nav/howto.svg';
import { ReactComponent as HelpIcon } from '../../../assets/icons/nav/help.svg';
import { ReactComponent as CloseIcon } from '../../../assets/icons/nav/close.svg';
import { ReactComponent as SignInIcon } from '../../../assets/icons/nav/signIn.svg';
import { ReactComponent as SignUpIcon } from '../../../assets/icons/nav/signUp.svg';
import { ReactComponent as ChatIcon } from '../../../assets/icons/nav/chat_icon.svg';
import { ReactComponent as NotificationsIcon } from '../../../assets/icons/nav/notification.svg';
import { ReactComponent as PersonIcon } from '../../../assets/icons/nav/person_icon.svg';
import { ReactComponent as WbSunnyIcon } from '../../../assets/icons/nav/sunIcon.svg';
import { ReactComponent as NightsStayIcon } from '../../../assets/icons/nav/moonIcon.svg';
import { formatDistanceToNow } from 'date-fns';
import { trackEvent } from '../../../analytics';

// Glassmorphism Drawer for mobile menu
const GlassDrawer = styled(Drawer)(({ theme }) => {
  // Use same background color as web menu
  const backgroundColor = '#00243ACC';

  return {
    '& .MuiDrawer-paper': {
      backgroundColor: backgroundColor,
      backdropFilter: 'none', // No blur - solid color to match header
      borderRadius: '0 0 32px 32px',
      border: 'none', // No border for seamless look
    },
  };
});

const MenuItemStyle = {
  fontFamily: 'Montserrat, sans-serif',
  color: '#FFFFFF',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '32px',
  p: '8px 24px',
  justifyContent: 'center',
  background: 'rgba(59, 59, 59, 0.18)',
  boxShadow: GlassShadows.glassUp,
  borderRadius: '32px',
  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
};

interface DispatchFromProps {
  logout: () => void;
  getCurrentUserDetails: () => void;
}

interface StateFromProps {
  authData?: AuthData;
  currentUserDetails?: UserDetailsViewModel;
  unreadChatsCount?: number;
}

interface PassedProps {}

interface NotificationType {
  id: number;
  type: 'message' | 'task' | 'event';
  title: string;
  description: string;
  timestamp: Date;
  read: boolean;
}

const mockNotifications: NotificationType[] = [
  {
    id: 1,
    type: 'message',
    title: 'New Message',
    description: 'Bob sent you a new message',
    timestamp: new Date(Date.now() - 1000 * 60 * 5),
    read: false,
  },
  {
    id: 2,
    type: 'task',
    title: 'Task Update',
    description: 'Your task "Fix the sink" has been accepted',
    timestamp: new Date(Date.now() - 1000 * 60 * 30),
    read: false,
  },
  {
    id: 3,
    type: 'event',
    title: 'New Event',
    description: 'Scheduled maintenance tomorrow at 2 PM',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
    read: true,
  },
];

// Role Toggle Component
const RoleToggleComponent: React.FC<{
  isHandyman: boolean;
  onToggle: () => void;
}> = ({ isHandyman, onToggle }) => {
  return (
    <Box
      onClick={onToggle}
      sx={{
        width: '93px',
        height: '48px',
        background: 'rgba(255, 255, 255, 0.001)',
        boxShadow:
          '0px 0.496757px 0.745136px rgba(255, 255, 255, 0.4), 0px -0.496757px 0.248379px rgba(0, 0, 0, 0.3), 0px 0px 34.773px rgba(0, 0, 0, 0.2), inset 0px 1.07796px 2.48379px #000000, inset 0px 2.07148px 3.72568px rgba(0, 0, 0, 0.4)',
        borderRadius: '64px',
        position: 'relative',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Left Icon - Briefcase (Orders) */}
      <Box
        sx={{
          position: 'absolute',
          width: '44px',
          height: '44px',
          left: '3px',
          top: '2px',
          background: !isHandyman ? 'rgba(255, 255, 255, 0.01)' : 'transparent',
          boxShadow: !isHandyman
            ? '0px 1.07796px 2.48379px #000000, 0px 2.07148px 3.72568px rgba(0, 0, 0, 0.4), inset 0px 0.496757px 0.745136px rgba(255, 255, 255, 0.4), inset 0px -0.496757px 0.248379px rgba(0, 0, 0, 0.3), inset 0px 0px 34.773px rgba(0, 0, 0, 0.2)'
            : 'none',
          borderRadius: '64px 0px 0px 64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: !isHandyman ? 1 : 0.3,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <WorkIcon
          sx={{
            fontSize: 24,
            color: '#00FFFF',
            filter: !isHandyman
              ? 'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))'
              : 'none',
          }}
        />
      </Box>

      {/* Right Icon - Build (Browse Handyman) */}
      <Box
        sx={{
          position: 'absolute',
          width: '44px',
          height: '44px',
          left: '47px',
          top: '2px',
          background: isHandyman ? 'rgba(255, 255, 255, 0.01)' : 'transparent',
          boxShadow: isHandyman
            ? '0px 1.07796px 2.48379px #000000, 0px 2.07148px 3.72568px rgba(0, 0, 0, 0.4), inset 0px 0.496757px 0.745136px rgba(255, 255, 255, 0.4), inset 0px -0.496757px 0.248379px rgba(0, 0, 0, 0.3), inset 0px 0px 34.773px rgba(0, 0, 0, 0.2)'
            : 'none',
          borderRadius: '0px 64px 64px 0px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: isHandyman ? 1 : 0.3,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <BuildIcon
          sx={{
            fontSize: 24,
            color: '#00FFFF',
            filter: isHandyman
              ? 'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))'
              : 'none',
          }}
        />
      </Box>
    </Box>
  );
};

// Theme Toggle Component

const slideToRightMobile = keyframes`
  from { transform: translateX(0px); }
  to { transform: translateX(24px); }
`;

const slideToLeftMobile = keyframes`
  from { transform: translateX(24px); }
  to { transform: translateX(0px); }
`;

const slideToRight = keyframes`
  from { transform: translateX(0px); }
  to { transform: translateX(30px); }
`;

const slideToLeft = keyframes`
  from { transform: translateX(30px); }
  to { transform: translateX(0px); }
`;

const iconUp = keyframes`
  from { transform: translateY(24px); opacity: 0; }
  to { transform: translateY(0px); opacity: 1; }
`;

const iconDown = keyframes`
  from { transform: translateY(0px); opacity: 1; }
  to { transform: translateY(-24px); opacity: 0; }
`;

const ThemeToggleComponent: React.FC = () => {
  const { mode, setMode } = useColorScheme();
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

  const isDark = mode === 'dark' || (mode === 'system' && prefersDarkMode);

  const handleToggle = () => {
    setMode(isDark ? 'light' : 'dark');
  };

  return (
    <Box
      onClick={handleToggle}
      className='toggleBar'
      sx={{
        width: { xs: '48px', tablet: '58px' },
        height: { xs: '24px', tablet: '28px' },
        background: 'rgba(255, 255, 255, 0.05)',
        boxShadow:
          '0px 0px 10px 0px rgba(0, 0, 0, 0.04), 0px -0.5px 0.25px 0px rgba(0, 0, 0, 0.3), 0px 0.5px 0.75px 0px rgba(255, 255, 255, 1), 0px 2.07px 3.73px 0px rgba(0, 0, 0, 0.27) inset, 0px 1.08px 2.48px 0px rgba(0, 0, 0, 0.25) inset',
        borderRadius: '64px',
        position: 'relative',
        cursor: 'pointer',
      }}
    >
      {/* Nob */}
      <Box
        sx={{
          position: 'absolute',
          width: { xs: '20px', tablet: '24px' },
          height: { xs: '20px', tablet: '24px' },
          left: '2px',
          top: '2px',
          background: 'rgba(255, 255, 255, 0.1)',
          boxShadow:
            'inset 0px 0px 34.77px 0px rgba(0, 0, 0, 0.2), inset 0px -0.5px 0.25px 0px rgba(0, 0, 0, 0.3), inset 0px 0.5px 0.75px 0px rgba(255, 255, 255, 0.4), 0px 2.07px 3.73px 0px rgba(0, 0, 0, 0.4), 0px 1.08px 2.48px 0px #000000',
          borderRadius: '64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',

          animation: {
            xs: `${
              isDark ? slideToRightMobile : slideToLeftMobile
            } 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards`,
            tablet: `${
              isDark ? slideToRight : slideToLeft
            } 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards`,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            width: '100%',
            height: '100%',
          }}
        >
          {/* Moon icon */}
          <Box
            sx={{
              display: 'flex',
              position: 'absolute',
              fontSize: 14,
              color: '#00FFFF',
              animation: `${isDark ? iconUp : iconDown} 0.3s forwards`,

              '& svg': {
                display: 'flex',
                width: { xs: '12px', tablet: '16px' },
                height: { xs: '12px', tablet: '16px' },
                filter: 'drop-shadow(0px 2px 6px rgba(0, 0, 0, 0.25))',
              },
            }}
          >
            <NightsStayIcon />
          </Box>

          {/* Sun icon */}
          <Box
            sx={{
              position: 'absolute',
              fontSize: 14,
              color: '#00FFFF',
              animation: `${!isDark ? iconUp : iconDown} 0.3s forwards`,

              '& svg': {
                display: 'flex',
                width: { xs: '12px', tablet: '16px' },
                height: { xs: '12px', tablet: '16px' },
                filter: 'drop-shadow(0px 2px 6px rgba(0, 0, 0, 0.25))',
              },
            }}
          >
            <WbSunnyIcon />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const AppBarComponent = (
  props: DispatchFromProps & StateFromProps & PassedProps,
) => {
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const [roleToggle, setRoleToggle] = React.useState(false); // false = client, true = handyman

  const navigate = useNavigate();

  const handleRoleToggle = () => {
    const newToggleState = !roleToggle;
    setRoleToggle(newToggleState);

    // Navigate based on the new state
    // roleToggle: false = Orders (briefcase), true = Browse Handyman (build icon)
    if (newToggleState) {
      // Switching to Browse Handyman mode (build icon)
      navigate('/browse-handyman');
    } else {
      // Switching to Orders mode (briefcase icon)
      navigate('/orders');
    }
  };

  const toggleDrawer = (newOpen: boolean) => () => {
    setIsDrawerOpen(newOpen);
  };

  React.useEffect(() => {
    if (props.authData) {
      props.getCurrentUserDetails();
    }
  }, [props.authData]);

  let isHandyman = props.authData?.role === 'handyman';
  let isCustomer = props.authData?.role === 'customer';
  let isLoggedIn = !!props.authData;

  const localAuthData = getAuthData();
  if (localAuthData && !props.authData) {
    isHandyman = localAuthData.role === 'handyman';
    isCustomer = localAuthData.role === 'customer';
    isLoggedIn = !!localAuthData;
  }

  const username = props.currentUserDetails
    ? `${props.currentUserDetails?.firstName} ${props.currentUserDetails?.lastName}`
    : 'Loading...';
  const avatar = props.currentUserDetails
    ? props.currentUserDetails?.avatarUrl
    : '';

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isProfileMenuOpen = Boolean(anchorEl);

  const headerRef = React.useRef<HTMLDivElement | null>(null);

  const handleProfileMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (headerRef.current && !anchorEl) {
      setAnchorEl(headerRef.current);
    } else {
      setAnchorEl(null);
    }
    // setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [notificationsAnchor, setNotificationsAnchor] =
    React.useState<null | HTMLElement>(null);
  const [servicesAnchor, setServicesAnchor] =
    React.useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = React.useState<MessageViewModel[]>(
    [],
  );
  const [hasPlayedSound, setHasPlayedSound] = React.useState(false);
  const unreadNotifications = notifications.filter((n) => !n.isViewed).length;

  const fetchNotifications = React.useCallback(async () => {
    const res = await api.notification.postNotification({
      currentPage: 1,
      pageSize: 5,
    });
    const data = res.data?.items ?? [];
    const prevUnread = notifications
      .filter((i) => !i.isViewed)
      .map((i) => i.id)
      .join(',');
    const nextUnread = data
      .filter((i) => !i.isViewed)
      .map((i) => i?.id)
      .join(',');
    if (
      prevUnread !== nextUnread &&
      !hasPlayedSound &&
      data.some((i) => !i.isViewed)
    ) {
      try {
        const AudioContextCtor: any =
          (window as any).AudioContext || (window as any).webkitAudioContext;
        const ctx = new AudioContextCtor();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.001, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.05, ctx.currentTime + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.3);
      } catch {}
      setHasPlayedSound(true);
    }
    setNotifications(data);
  }, []);

  React.useEffect(() => {
    if (isLoggedIn) {
      fetchNotifications();
      const id = setInterval(fetchNotifications, 15000);
      return () => clearInterval(id);
    }
  }, [isLoggedIn, fetchNotifications]);

  const handleNotificationsClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchor(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchor(null);
  };

  const isServicesMenuOpen = Boolean(servicesAnchor);
  const handleServicesClick = (event: React.MouseEvent<HTMLElement>) => {
    setServicesAnchor(event.currentTarget);
  };
  const handleServicesClose = () => {
    setServicesAnchor(null);
  };

  const [guestMenuAnchor, setGuestMenuAnchor] =
    React.useState<null | HTMLElement>(null);
  const isGuestMenuOpen = Boolean(guestMenuAnchor);
  const handleGuestMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // setGuestMenuAnchor(event.currentTarget);
    if (headerRef.current && !guestMenuAnchor) {
      setGuestMenuAnchor(headerRef.current);
    } else {
      setGuestMenuAnchor(null);
    }
  };
  const handleGuestMenuClose = () => {
    setGuestMenuAnchor(null);
  };

  const isMobile = useMediaQuery((theme: any) =>
    theme.breakpoints.down('tablet'),
  );
  const headerWidth = Math.round(
    headerRef.current?.getBoundingClientRect().width || 0,
  );

  const current = props.currentUserDetails;

  const shouldVerify =
    isLoggedIn &&
    ((current?.kycStatus !== 'Verified' && current?.kycStatus !== undefined) ||
      (isHandyman && current?.isStripeAccountVerified === false));

  return (
    <>
      {/* Main Desktop Header */}
      <Box
        sx={{
          position: 'fixed',
          top: '24px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1900,
          maxWidth: '100%',
          // display: { xs: 'none', md: 'block' },
        }}
      >
        <GlassHeaderContainer
          ref={headerRef}
          isLoggedIn={isLoggedIn}
          isMenuOpened={isDrawerOpen || isGuestMenuOpen || isProfileMenuOpen}
        >
          {/* Left Section: Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '25px' }}>
            <GlassLogo
              onClick={() => navigate('/')}
              sx={{
                display: {
                  xs:
                    isDrawerOpen || isGuestMenuOpen || isProfileMenuOpen
                      ? 'flex'
                      : 'none',
                  tablet: 'flex',
                },
              }}
            >
              <Sitemark />
            </GlassLogo>

            <GlassLogo
              onClick={() => navigate('/')}
              sx={{
                display: {
                  xs:
                    isDrawerOpen || isGuestMenuOpen || isProfileMenuOpen
                      ? 'none'
                      : 'flex',
                  tablet: 'none',
                },
              }}
            >
              <Box
                component='img'
                src='/logo-white.svg'
                alt='Word of Mouth LTD'
                sx={{
                  height: 40,
                  width: 'auto',
                  mr: 1,
                  display: 'block',
                  filter:
                    'brightness(0) saturate(100%) invert(81%) sepia(82%) saturate(2456%) hue-rotate(143deg) brightness(103%) contrast(106%)', // Cyan color #00FFFF
                }}
              />
            </GlassLogo>
          </Box>

          {/* Right Section: User Menu */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              mr: {
                xs:
                  isDrawerOpen || isGuestMenuOpen || isProfileMenuOpen
                    ? '4px'
                    : '-12px',
                tablet: 0,
              },
              '& .toggleBar': {
                mr: { xs: 0, tablet: isGuestMenuOpen ? '286px' : 0 },
              },
              transition: 'margin .2s',
            }}
          >
            {!isLoggedIn && (
              <>
                {/* Theme Toggle for non-logged in users */}
                <ThemeToggleComponent />

                {/* Sign In Button */}

                {!isGuestMenuOpen && (
                  <Button
                    onClick={() => {
                      trackEvent('navigate_sign_in', { source: 'header' });
                      navigate('/sign-in');
                    }}
                    sx={{
                      // display: { xs: 'none', md: 'flex' },
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: { xs: '2px 11px', tablet: '8px 24px' },
                      gap: '8px',
                      height: { xs: '32px', tablet: '48px' },
                      border: 0,
                      background: 'rgba(59, 59, 59, 0.18)',
                      boxShadow: GlassShadows.glassUp,
                      borderRadius: '64px',
                      textTransform: 'none',
                      fontFamily: 'Montserrat',
                      fontWeight: 500,
                      fontSize: { xs: '12px', tablet: '16px' },
                      lineHeight: { xs: '24px', tablet: '32px' },
                      color: '#FFFFFF',
                      whiteSpace: 'nowrap',
                      minWidth: 'auto',
                      '&:hover': {
                        background: 'rgba(59, 59, 59, 0.18)',
                        boxShadow: GlassShadows.buttonHover,
                      },
                      '&:active': {
                        boxShadow: GlassShadows.buttonActive,
                        '& svg path': {
                          fill: GlassColors.accent,
                        },
                      },
                    }}
                  >
                    Sign In
                  </Button>
                )}
                {/* Menu Icon Button - Desktop: opens guest menu */}
                <IconButton
                  onClick={handleGuestMenuClick}
                  sx={{
                    // display: { xs: 'none', md: 'flex' },
                    width: { xs: '32px', tablet: '48px' },
                    height: { xs: '32px', tablet: '48px' },
                    borderRadius: '64px',
                    background: 'rgba(59, 59, 59, 0.18) !important',
                    boxShadow: GlassShadows.glassUp,
                    border: 0,
                    padding: 0,
                    transition: 'box-shadow .2s ease',
                    '&:hover': {
                      background: 'rgba(0, 36, 58, 0.8)',
                      boxShadow: GlassShadows.buttonHover,
                    },
                    '&:active': {
                      boxShadow: GlassShadows.buttonActive,
                      '& svg path': {
                        fill: GlassColors.accent,
                      },
                    },
                    '& svg': {
                      width: { xs: '16px', tablet: '23px' },
                      height: { xs: '16px', tablet: '23px' },
                      color: '#E2F7FF !important',
                    },
                  }}
                >
                  {isGuestMenuOpen ? (
                    <CloseIcon />
                  ) : (
                    <MenuIcon sx={{ color: '#E2F7FF !important' }} />
                  )}
                </IconButton>
              </>
            )}
            {isLoggedIn && (
              <>
                {/* Theme Toggle for logged in users */}
                <ThemeToggleComponent />
                {/* Chat Icon */}

                {(!isProfileMenuOpen || !isMobile) && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '8px',
                    }}
                  >
                    <IconButton
                      onClick={() => navigate('chats')}
                      sx={{
                        width: { xs: '32px', tablet: '48px' },
                        height: { xs: '32px', tablet: '48px' },
                        borderRadius: '64px',
                        border: 0,
                        background: 'rgba(59, 59, 59, 0.18)!important',
                        boxShadow: GlassShadows.glassUp,
                        transition: 'box-shadow .2s',
                        '& svg': {
                          width: {
                            xs: '16px',
                            tablet: '24px',
                          },
                          height: {
                            xs: '16px',
                            tablet: '24px',
                          },
                        },
                        '&:hover': {
                          background: 'rgba(0, 36, 58, 0.8)',
                          boxShadow: GlassShadows.buttonHover,
                        },
                        '&:active': {
                          boxShadow: GlassShadows.buttonActive,
                          '& svg path': {
                            fill: GlassColors.accent,
                          },
                        },
                      }}
                    >
                      <Badge
                        badgeContent={props.unreadChatsCount || 0}
                        color='error'
                      >
                        <ChatIcon />
                      </Badge>
                    </IconButton>

                    {/* Notifications Icon */}
                    <IconButton
                      onClick={handleNotificationsClick}
                      sx={{
                        width: { xs: '32px', tablet: '48px' },
                        height: { xs: '32px', tablet: '48px' },
                        border: 0,
                        background: 'rgba(59, 59, 59, 0.18)!important',
                        boxShadow: GlassShadows.glassUp,
                        borderRadius: '64px',
                        transition: 'box-shadow .2s',
                        '& svg': {
                          width: {
                            xs: '16px',
                            tablet: '24px',
                          },
                          height: {
                            xs: '16px',
                            tablet: '24px',
                          },
                        },
                        '&:hover': {
                          background: 'rgba(0, 36, 58, 0.8)',
                          boxShadow: GlassShadows.buttonHover,
                        },
                        '&:active': {
                          boxShadow: GlassShadows.buttonActive,
                          '& svg path': {
                            fill: GlassColors.accent,
                          },
                        },
                      }}
                    >
                      <Badge badgeContent={unreadNotifications} color='error'>
                        <NotificationsIcon />
                      </Badge>
                    </IconButton>

                    {/* User Profile Button with Username - Desktop: navigates to profile */}
                    <Button
                      onClick={() => navigate('profile')}
                      sx={{
                        // display: { xs: 'none', md: 'flex' },
                        flexDirection: 'row',
                        justifyContent: 'start',
                        alignItems: 'center',
                        padding: {
                          xs: '8px',
                          mobile: '8px 12px',
                          tablet: '8px 24px',
                        },
                        gap: '8px',
                        maxWidth: { xs: '106px', tablet: 'unset' },
                        overflow: 'hidden',
                        height: { xs: '32px', tablet: '48px' },
                        border: 0,
                        background: 'rgba(59, 59, 59, 0.18)!important',
                        boxShadow: GlassShadows.glassUp,
                        borderRadius: '64px',
                        textTransform: 'none',
                        fontFamily: 'Montserrat',
                        fontWeight: 500,
                        fontSize: { xs: '12px', tablet: '16px' },
                        lineHeight: '32px',
                        color: '#FFFFFF',
                        whiteSpace: 'nowrap',
                        minWidth: 'auto',
                        '& span': {
                          margin: { xs: 0, mr: '8px' },
                        },
                        '& svg': {
                          width: {
                            xs: '16px',
                            tablet: '24px',
                          },
                          height: {
                            xs: '16px',
                            tablet: '24px',
                          },
                        },
                        '&:hover': {
                          background: 'rgba(0, 36, 58, 0.8)',
                          boxShadow: GlassShadows.buttonHover,
                        },
                        '&:active': {
                          boxShadow: GlassShadows.buttonActive,
                          '& svg path': {
                            fill: GlassColors.accent,
                          },
                        },
                      }}
                      startIcon={<PersonIcon />}
                    >
                      <Box
                        sx={{
                          maxWidth: {
                            xs: '80px',
                            tablet: 'unset',
                          },
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          display: { xs: 'none', mobile: 'block' },
                        }}
                      >
                        {username}
                      </Box>
                    </Button>
                  </Box>
                )}

                {/* Menu Icon Button - Desktop: opens menu with Profile, Help, Logout */}
                <IconButton
                  onClick={handleProfileMenuClick}
                  sx={{
                    // display: { xs: 'none', md: 'flex' },
                    width: { xs: '32px', tablet: '48px' },
                    height: { xs: '32px', tablet: '48px' },
                    border: 0,
                    background: 'rgba(59, 59, 59, 0.18)!important',
                    boxShadow: GlassShadows.glassUp,
                    borderRadius: '64px',
                    padding: 0,
                    transition: 'box-shadow .2s',
                    '& svg': {
                      width: {
                        xs: '16px',
                        tablet: '24px',
                      },
                      height: {
                        xs: '16px',
                        tablet: '24px',
                      },
                    },
                    '&:hover': {
                      background: 'rgba(0, 36, 58, 0.8)',
                      boxShadow: GlassShadows.buttonHover,
                    },
                    '&:active': {
                      boxShadow: GlassShadows.buttonActive,
                      '& svg path': {
                        fill: GlassColors.accent,
                      },
                    },
                  }}
                >
                  {isProfileMenuOpen ? (
                    <CloseIcon />
                  ) : (
                    <MenuIcon sx={{ color: '#E2F7FF !important' }} />
                  )}
                </IconButton>

                {/* Menu Icon Button - Mobile Only: opens full drawer */}
                {/* <IconButton
                  onClick={toggleDrawer(true)}
                  sx={{
                    display: { xs: 'flex', md: 'none' },
                    width: '48px',
                    height: '48px',
                    background: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(70, 90, 100, 0.4)'
                        : 'rgba(0, 36, 58, 0.8)',
                    borderRadius: '64px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    padding: 0,
                    '&:hover': {
                      background: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(70, 90, 100, 0.5)'
                          : 'rgba(0, 36, 58, 0.9)',
                    },
                  }}
                >
                  <MenuIcon sx={{ color: '#E2F7FF !important' }} />
                </IconButton> */}
              </>
            )}
          </Box>

          {/* User Menu */}
          {isLoggedIn && (
            <Menu
              anchorEl={anchorEl}
              open={isProfileMenuOpen}
              onClose={handleClose}
              slotProps={{
                paper: {
                  sx: (theme) => ({
                    '&': {
                      background:
                        theme.palette.mode === 'light'
                          ? 'rgba(0, 36, 58, 0.8) '
                          : 'rgba(0, 92, 173, 0.7) ',
                    },
                    boxShadow: {
                      xs: GlassShadows.menuMobile,
                      tablet: GlassShadows.glassUp,
                    },
                    backdropFilter: 'blur(6px)',
                    borderRadius: '32px',
                    mt: { xs: '-64px', tablet: '8px' },
                    border: 0,
                    pt: { xs: '64px', tablet: 0 },
                    maxWidth: 'none',
                    flexShrink: 0,
                    width: {
                      xs: headerWidth ? `${headerWidth}px` : '100%',
                      tablet: '424px',
                    },

                    '& ul': {
                      p: '12px 24px 24px !important',
                    },
                  }),
                },
              }}
              disableScrollLock
              marginThreshold={0}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: isMobile ? 'left' : 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: isMobile ? 'left' : 'right',
              }}
            >
              {isProfileMenuOpen && isMobile && (
                <MenuItem
                  sx={{
                    p: 0,
                    mb: '12px',
                    '&:hover': { background: 'none' },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'space-between',
                      gap: '8px',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '8px',
                      }}
                    >
                      <IconButton
                        onClick={() => {
                          setAnchorEl(null);
                          navigate('chats');
                        }}
                        sx={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '64px',
                          border: 0,
                          background: 'rgba(59, 59, 59, 0.18)!important',
                          boxShadow: GlassShadows.glassUp,
                          transition: 'box-shadow .2s',
                          '& svg': {
                            width: '24px',
                            height: '24px',
                          },
                          '&:hover': {
                            background: 'rgba(0, 36, 58, 0.8)',
                            boxShadow: GlassShadows.buttonHover,
                          },
                          '&:active': {
                            boxShadow: GlassShadows.buttonActive,
                            '& svg path': {
                              fill: GlassColors.accent,
                            },
                          },
                        }}
                      >
                        <Badge
                          badgeContent={props.unreadChatsCount || 0}
                          color='error'
                        >
                          <ChatIcon />
                        </Badge>
                      </IconButton>

                      {/* Notifications Icon */}
                      <IconButton
                        onClick={handleNotificationsClick}
                        sx={{
                          width: '48px',
                          height: '48px',
                          border: 0,
                          background: 'rgba(59, 59, 59, 0.18)!important',
                          boxShadow: GlassShadows.glassUp,
                          borderRadius: '64px',
                          transition: 'box-shadow .2s',
                          '& svg': {
                            width: '24px',
                            height: '24px',
                          },
                          '&:hover': {
                            background: 'rgba(0, 36, 58, 0.8)',
                            boxShadow: GlassShadows.buttonHover,
                          },
                          '&:active': {
                            boxShadow: GlassShadows.buttonActive,
                            '& svg path': {
                              fill: GlassColors.accent,
                            },
                          },
                        }}
                      >
                        <Badge badgeContent={unreadNotifications} color='error'>
                          <NotificationsIcon />
                        </Badge>
                      </IconButton>
                    </Box>

                    {/* User Profile Button with Username - Desktop: navigates to profile */}
                    <Button
                      onClick={() => {
                        setAnchorEl(null);
                        navigate('profile');
                      }}
                      sx={{
                        // display: { xs: 'none', md: 'flex' },
                        flexDirection: 'row',
                        justifyContent: 'start',
                        alignItems: 'center',
                        padding: '8px 24px',
                        gap: '8px',
                        overflow: 'hidden',
                        height: '48px',
                        border: 0,
                        background: 'rgba(59, 59, 59, 0.18)!important',
                        boxShadow: GlassShadows.glassUp,
                        transition: 'box-shadow .2s',
                        borderRadius: '64px',
                        textTransform: 'none',
                        fontFamily: 'Montserrat',
                        fontWeight: 500,
                        fontSize: '16px',
                        lineHeight: '32px',
                        color: '#FFFFFF',
                        whiteSpace: 'nowrap',
                        minWidth: 'auto',
                        '& span': {
                          margin: { xs: 0, mr: '8px' },
                        },
                        '& svg': {
                          width: '24px',
                          height: '24px',
                        },
                        '&:hover': {
                          background: 'rgba(0, 36, 58, 0.8)',
                          boxShadow: GlassShadows.buttonHover,
                        },
                        '&:active': {
                          boxShadow: GlassShadows.buttonActive,
                          '& svg path': {
                            fill: GlassColors.accent,
                          },
                        },
                      }}
                      startIcon={<PersonIcon />}
                    >
                      <Box
                        sx={{
                          maxWidth: { xs: '75px', mobile: 'unset' },
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                        }}
                      >
                        {username}
                      </Box>
                    </Button>
                  </Box>
                </MenuItem>
              )}
              {isHandyman && (
                <>
                  <MenuItem
                    onClick={() => {
                      handleClose();
                      navigate('orders');
                    }}
                    sx={{
                      ...MenuItemStyle,
                      mb: '8px',
                      mt: '12px',
                    }}
                  >
                    <Box
                      component='span'
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mr: 1,
                      }}
                    >
                      <FindWorkIcon style={{ width: 20, height: 20 }} />
                    </Box>
                    Find Work
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleClose();
                      navigate('proposals');
                    }}
                    sx={{
                      ...MenuItemStyle,
                      mb: '8px',
                    }}
                  >
                    <Box
                      component='span'
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mr: 1,
                      }}
                    >
                      <SuitcaseIcon style={{ width: 20, height: 20 }} />
                    </Box>
                    My Proposals
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleClose();
                      navigate('orders');
                    }}
                    sx={{
                      ...MenuItemStyle,
                      mb: '24px',
                    }}
                  >
                    <Box
                      component='span'
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mr: 1,
                      }}
                    >
                      <MyTasksIcon style={{ width: 20, height: 20 }} />
                    </Box>
                    My Tasks
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleClose();
                      navigate('profile');
                    }}
                    sx={{
                      ...MenuItemStyle,
                      mb: '24px',
                    }}
                  >
                    <Box
                      component='span'
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mr: 1,
                      }}
                    >
                      <PortfolioIcon style={{ width: 20, height: 20 }} />
                    </Box>
                    Portfolio
                  </MenuItem>
                </>
              )}

              {isCustomer && (
                <>
                  <MenuItem
                    onClick={() => {
                      handleClose();
                      navigate('orders');
                    }}
                    sx={{
                      ...MenuItemStyle,
                      mb: '8px',
                      mt: '12px',
                    }}
                  >
                    <Box
                      component='span'
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mr: 1,
                      }}
                    >
                      <MyTasksIcon style={{ width: 20, height: 20 }} />
                    </Box>
                    My Tasks
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleClose();
                      navigate('browse-handyman');
                    }}
                    sx={{
                      ...MenuItemStyle,
                      mb: '8px',
                    }}
                  >
                    <Box
                      component='span'
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mr: 1,
                      }}
                    >
                      <BrowseProfIcon style={{ width: 20, height: 20 }} />
                    </Box>
                    Browse Professionals
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleClose();
                      shouldVerify
                        ? navigate('/verification')
                        : navigate('create-order');
                    }}
                    sx={{
                      ...MenuItemStyle,
                      mb: '24px',
                    }}
                  >
                    <Box
                      component='span'
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mr: 1,
                      }}
                    >
                      <CreateOrderIcon style={{ width: 20, height: 20 }} />
                    </Box>
                    {shouldVerify
                      ? 'Verify To Create New Order'
                      : 'Create New Order'}
                  </MenuItem>
                </>
              )}

              <MenuItem
                onClick={() => {
                  props.logout();
                  handleClose();
                }}
                sx={{
                  ...MenuItemStyle,
                  mb: '8px',
                }}
              >
                <Box
                  component='span'
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}
                >
                  <LogoutIcon style={{ width: 20, height: 20 }} />
                </Box>
                Log Out
              </MenuItem>
            </Menu>
          )}
        </GlassHeaderContainer>
        {shouldVerify && (
          <Box
            sx={{
              display: isProfileMenuOpen ? 'none' : 'block',
              position: { xs: 'static', tablet: 'absolute' },
              zIndex: 0,
              right: 0,
              top: '100%',
              p: { xs: '16px', tablet: '24px' },
              background: 'rgba(132, 132, 132, 0.3)',
              borderRadius: '32px',
              backdropFilter: 'blur(12px)',
              textAlign: { xs: 'center', tablet: 'right' },
              width: { xs: '100%', tablet: '309px' },
              maxWidth: 'round(nearest, 95%, 1px)',
              m: { xs: '24px auto 0', tablet: '16px -55px 0' },
              boxShadow: `
                  0px 0px 16px 0px rgba(92, 92, 92, 0.3) inset,
                  0px 0px 3px 0px rgba(255, 255, 255, 0.4) inset,
                  -1px -1px 0.5px -1px rgba(255, 255, 255, 1) inset,
                  1px 1px 0.5px -1px rgba(255, 255, 255, 0.4) inset,
                  -1px -1px 0px -0.5px rgba(255, 255, 255, 0.5) inset,
                  1px 1px 0px -0.5px rgba(255, 255, 255, 0.6) inset,
                  0px 1px 8px 0px rgba(0, 0, 0, 0.12),
                  0px 0px 2px 0px rgba(0, 0, 0, 0.1)
                `,
              '@media (min-width: 768px) and (max-width: 1100px)': {
                mr: '24px',
              },
            }}
          >
            <Typography
              component='h4'
              sx={{
                fontFamily: {
                  xs: 'Roboto, sans-serif',
                  tablet: 'Montserrat, sans-serif',
                },
                fontSize: { xs: 16, tablet: 20 },
                fontWeight: { xs: 700, tablet: 800 },
                lineHeight: { xs: '175%', tablet: '160%' },
                letterSpacing: '.15px',
                mb: { xs: '8px', tablet: '16px' },
                color: (theme) =>
                  theme.palette.mode === 'light'
                    ? GlassColors.secondary
                    : GlassColors.accent,
              }}
            >
              Unlock Full Access
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 400,
                fontSize: 16,
                lineHeight: '150%',
                letterSpacing: '.15px',
                mb: { xs: '8px', tablet: '16px' },
                color: (theme) =>
                  theme.palette.mode === 'light'
                    ? GlassColors.light.textPrimary
                    : GlassColors.dark.textPrimary,
              }}
            >
              Verify your profile to activate all features.
            </Typography>
            <GlassButton
              onClick={() => navigate('/verification')}
              sx={{
                '&&': {
                  height: { xs: '32px', tablet: '40px' },
                  p: '6px 16px',
                  fontSize: { xs: 12, tablet: 14 },
                  ml: 'auto',
                },
              }}
            >
              Verify Now
            </GlassButton>
          </Box>
        )}
      </Box>

      {/* Services Menu */}
      <Menu
        anchorEl={servicesAnchor}
        open={isServicesMenuOpen}
        onClose={handleServicesClose}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        {!isLoggedIn && (
          <>
            <MenuItem
              onClick={() => {
                handleServicesClose();
                navigate('browse-handyman');
              }}
            >
              Browse Services
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleServicesClose();
                navigate('sign-up');
              }}
            >
              Become a Tasker
            </MenuItem>
          </>
        )}

        {isCustomer && (
          <>
            <MenuItem
              onClick={() => {
                handleServicesClose();
                navigate('browse-handyman');
              }}
            >
              Browse Handyman
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleServicesClose();
                navigate('orders');
              }}
            >
              My Orders
            </MenuItem>
          </>
        )}

        {isHandyman && (
          <MenuItem
            onClick={() => {
              handleServicesClose();
              navigate('orders');
            }}
          >
            My Tasks
          </MenuItem>
        )}
      </Menu>
      {/* Guest Menu (Not Logged In) */}
      <Menu
        anchorEl={guestMenuAnchor}
        open={isGuestMenuOpen}
        onClose={handleGuestMenuClose}
        sx={{ zIndex: shouldVerify ? 1300 : 1400 }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              '&': {
                background:
                  theme.palette.mode === 'light'
                    ? 'rgba(0, 36, 58, 0.8) '
                    : 'rgba(0, 92, 173, 0.7) ',
              },
              boxShadow: {
                xs: GlassShadows.menuMobile,
                tablet: GlassShadows.glassUp,
              },
              backdropFilter: 'blur(6px)',
              borderRadius: '32px',
              mt: { xs: '-64px', tablet: '8px' },
              border: 0,
              pt: { xs: '64px', tablet: 0 },
              maxWidth: 'none',
              flexShrink: 0,
              width: {
                xs: headerWidth ? `${headerWidth}px` : '100%',
                tablet: '424px',
              },

              '& ul': {
                p: {
                  xs: '7px 24px 24px !important',
                  tablet: '24px !important',
                },
              },
            }),
          },
        }}
        disableScrollLock
        marginThreshold={0}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isMobile ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isMobile ? 'left' : 'right',
        }}
      >
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', mb: '24px' }}
        >
          <Link
            href='sign-in'
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              color: '#FFFFFF',
              fontWeight: 500,
              fontSize: {
                xs: '14px',
                tablet: '16px',
              },
              lineHeight: '32px',
              p: { xs: '5px 16px', tablet: '8px 24px' },
              justifyContent: 'center',
              background: 'rgba(59, 59, 59, 0.18)',
              boxShadow: GlassShadows.glassUp,
              borderRadius: '32px',
              transition: 'background-color .2s',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              '&:before': {
                display: 'none',
              },
              '& svg': {
                width: {
                  xs: '20px',
                  tablet: 'unset',
                },
                height: {
                  xs: '20px',
                  tablet: 'unset',
                },
              },
            }}
          >
            <SignInIcon />
            Sign in
          </Link>
          <Link
            href='sign-up'
            variant='body2'
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              color: '#FFFFFF',
              fontWeight: 500,
              fontSize: {
                xs: '14px',
                tablet: '16px',
              },
              lineHeight: '32px',
              p: { xs: '5px 16px', tablet: '8px 24px' },
              justifyContent: 'center',
              background: 'rgba(59, 59, 59, 0.18)',
              boxShadow: GlassShadows.glassUp,
              borderRadius: '32px',
              transition: 'background-color .2s',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              '&:before': {
                display: 'none',
              },
              '& svg': {
                width: {
                  xs: '20px',
                  tablet: 'unset',
                },
                height: {
                  xs: '20px',
                  tablet: 'unset',
                },
              },
            }}
          >
            <SignUpIcon /> Sign up
          </Link>
        </Box>
        <MenuItem
          onClick={() => {
            handleGuestMenuClose();
            navigate('/');
          }}
          sx={{
            ...MenuItemStyle,
            mb: '8px',
          }}
        >
          <Box
            component='span'
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}
          >
            <HomeIcon style={{ width: 20, height: 20 }} />
          </Box>
          Home
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleGuestMenuClose();
            navigate('browse-handyman');
          }}
          sx={{
            ...MenuItemStyle,
            mb: '8px',
          }}
        >
          <Box
            component='span'
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}
          >
            <FindProIcon style={{ width: 20, height: 20 }} />
          </Box>
          Find a Professional
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleGuestMenuClose();
            navigate('sign-up');
          }}
          sx={{
            ...MenuItemStyle,
            mb: '24px',
          }}
        >
          <Box
            component='span'
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}
          >
            <BecomeTaskerIcon style={{ width: 20, height: 20 }} />
          </Box>
          Become a Provider
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleGuestMenuClose();
            navigate('how-it-works');
          }}
          sx={{
            ...MenuItemStyle,
            mb: '8px',
          }}
        >
          <Box
            component='span'
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}
          >
            <HowToIcon style={{ width: 20, height: 20 }} />
          </Box>
          How It Works
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleGuestMenuClose();
            navigate('support');
          }}
          sx={{
            ...MenuItemStyle,
            mb: '8px',
          }}
        >
          <Box
            component='span'
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}
          >
            <HelpIcon style={{ width: 20, height: 20 }} />
          </Box>
          Help Center
        </MenuItem>
      </Menu>

      {/* Mobile Header - Single component with drawer */}
      {/*
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          display: { xs: 'block', md: 'none' },
        }}
      >
        <GlassAppBar
          position='relative'
          sx={{
            boxShadow: 0,
            // Remove bottom border radius when drawer is open to make it seamless
            borderRadius: isDrawerOpen ? '0' : '0 0 32px 32px',
            // Remove blur and make solid when drawer is open
            backdropFilter: isDrawerOpen ? 'none' : 'blur(6px)',
            backgroundColor: '#00243ACC !important',
          }}
        >
          <Container maxWidth='lg'>
            <GlassToolbar
              variant='dense'
              disableGutters
              sx={{ minHeight: '64px !important' }}
            >
              <GlassLogo onClick={() => navigate('/')}>
                <Sitemark />
              </GlassLogo>

              <Box sx={{ flexGrow: 1 }} />

              <IconButton
                aria-label='Menu button'
                onClick={toggleDrawer(true)}
                sx={{
                  width: '40px',
                  height: '40px',
                  background: 'rgba(70, 90, 100, 0.4) !important',
                  borderRadius: '64px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  padding: 0,
                  '&:hover': {
                    background: 'rgba(70, 90, 100, 0.5) !important',
                  },
                  '& svg': {
                    color: '#E2F7FF !important',
                  },
                }}
              >
                <MenuIcon sx={{ color: '#E2F7FF !important' }} />
              </IconButton>
            </GlassToolbar>
          </Container>
*/}

      {/* Drawer as part of the same component - positioned directly below header */}
      {/* <GlassDrawer
            anchor='top'
            open={isDrawerOpen}
            onClose={toggleDrawer(false)}
            PaperProps={{
              sx: {
                top: '64px', // Match header height exactly (GlassToolbar variant="dense" is 64px)
                backgroundColor: '#00243ACC !important',
                backdropFilter: 'none !important', // No blur - solid color to match header
                borderRadius: '0 0 32px 32px !important', // Only round the bottom corners
                boxShadow: 'none !important', // Remove shadow to make it seamless
                border: 'none !important', // Remove borders
                borderTop: 'none !important', // No top border to connect seamlessly
                marginTop: 0, // No gap
              },
            }}
          >
            <Box
              sx={{
                p: 2,
                pb: 3,
                minHeight: 'auto',
              }}
        > */}

      {/* Theme Toggle */}

      {/* <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <ThemeToggleComponent />
              </Box> */}

      {/* Mobile Menu Items */}

      {/* {!isLoggedIn && (
                <>
                  <MenuItem
                    onClick={() => {
                      navigate('/');
                      toggleDrawer(false)();
                    }}
                    sx={{
                      color: '#FFFFFF',
                      fontSize: '18px',
                      fontWeight: 400,
                      py: 1.5,
                      px: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <HomeIcon style={{ width: 24, height: 24 }} />
                    Home
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      navigate('browse-handyman');
                      toggleDrawer(false)();
                    }}
                    sx={{
                      color: '#FFFFFF',
                      fontSize: '18px',
                      fontWeight: 400,
                      py: 1.5,
                      px: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <FindProIcon style={{ width: 24, height: 24 }} />
                    Find a Professional
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      trackEvent("navigate_sign_up", { source: "drawer", cta: "become_provider" });
                      navigate('sign-up');
                      toggleDrawer(false)();
                    }}
                    sx={{
                      color: '#FFFFFF',
                      fontSize: '18px',
                      fontWeight: 400,
                      py: 1.5,
                      px: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <BecomeTaskerIcon style={{ width: 24, height: 24 }} />
                    Become a Provider
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      navigate('how-it-works');
                      toggleDrawer(false)();
                    }}
                    sx={{
                      color: '#FFFFFF',
                      fontSize: '18px',
                      fontWeight: 400,
                      py: 1.5,
                      px: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <HowToIcon style={{ width: 24, height: 24 }} />
                    How It Works
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      navigate('support');
                      toggleDrawer(false)();
                    }}
                    sx={{
                      color: '#FFFFFF',
                      fontSize: '18px',
                      fontWeight: 400,
                      py: 1.5,
                      px: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <HelpIcon style={{ width: 24, height: 24 }} />
                    Help Center
                  </MenuItem>
                  <Divider
                    sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.2)' }}
                  />
                  <MenuItem
                    onClick={() => {
                      trackEvent("navigate_sign_in", { source: "drawer" });
                      navigate('sign-in');
                      toggleDrawer(false)();
                    }}
                    sx={{
                      color: '#FFFFFF',
                      fontSize: '18px',
                      fontWeight: 400,
                      py: 1.5,
                      px: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <PersonIcon
                      sx={{
                        width: 24,
                        height: 24,
                        color: '#FFFFFF',
                        backgroundColor: 'transparent',
                      }}
                    />
                    Sign In
                  </MenuItem>
                </>
              )} */}

      {/* {isLoggedIn && (
                <>
                  <MenuItem
                    onClick={() => {
                      navigate('/');
                      toggleDrawer(false)();
                    }}
                    sx={{
                      color: '#FFFFFF',
                      fontSize: '18px',
                      fontWeight: 400,
                      py: 1.5,
                      px: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <HomeIcon style={{ width: 24, height: 24 }} />
                    Home
                  </MenuItem>

                  {isHandyman && (
                    <>
                      <MenuItem
                        onClick={() => {
                          navigate('orders');
                          toggleDrawer(false)();
                        }}
                        sx={{
                          color: '#FFFFFF',
                          fontSize: '18px',
                          fontWeight: 400,
                          py: 1.5,
                          px: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          },
                        }}
                      >
                        <FindWorkIcon style={{ width: 24, height: 24 }} />
                        Find Work
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          navigate('orders');
                          toggleDrawer(false)();
                        }}
                        sx={{
                          color: '#FFFFFF',
                          fontSize: '18px',
                          fontWeight: 400,
                          py: 1.5,
                          px: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          },
                        }}
                      >
                        <SuitcaseIcon style={{ width: 24, height: 24 }} />
                        My Proposals
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          navigate('orders');
                          toggleDrawer(false)();
                        }}
                        sx={{
                          color: '#FFFFFF',
                          fontSize: '18px',
                          fontWeight: 400,
                          py: 1.5,
                          px: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          },
                        }}
                      >
                        <MyTasksIcon style={{ width: 24, height: 24 }} />
                        My Tasks
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          navigate('profile');
                          toggleDrawer(false)();
                        }}
                        sx={{
                          color: '#FFFFFF',
                          fontSize: '18px',
                          fontWeight: 400,
                          py: 1.5,
                          px: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          },
                        }}
                      >
                        <PortfolioIcon style={{ width: 24, height: 24 }} />
                        Portfolio
                      </MenuItem>
                    </>
                  )}

                  {isCustomer && (
                    <>
                      <MenuItem
                        onClick={() => {
                          navigate('orders');
                          toggleDrawer(false)();
                        }}
                        sx={{
                          color: '#FFFFFF',
                          fontSize: '18px',
                          fontWeight: 400,
                          py: 1.5,
                          px: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          },
                        }}
                      >
                        <MyTasksIcon style={{ width: 24, height: 24 }} />
                        My Tasks
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          navigate('browse-handyman');
                          toggleDrawer(false)();
                        }}
                        sx={{
                          color: '#FFFFFF',
                          fontSize: '18px',
                          fontWeight: 400,
                          py: 1.5,
                          px: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          },
                        }}
                      >
                        <BrowseProfIcon style={{ width: 24, height: 24 }} />
                        Browse Professionals
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          navigate('create-order');
                          toggleDrawer(false)();
                        }}
                        sx={{
                          color: '#FFFFFF',
                          fontSize: '18px',
                          fontWeight: 400,
                          py: 1.5,
                          px: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          },
                        }}
                      >
                        <CreateOrderIcon style={{ width: 24, height: 24 }} />
                        Create New Order
                      </MenuItem>
                    </>
                  )}

                  <Divider
                    sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.2)' }}
                  />
                  <MenuItem
                    onClick={() => {
                      props.logout();
                      toggleDrawer(false)();
                    }}
                    sx={{
                      color: '#FFFFFF',
                      fontSize: '18px',
                      fontWeight: 400,
                      py: 1.5,
                      px: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <LogoutIcon style={{ width: 24, height: 24 }} />
                    Log Out
                  </MenuItem>
                </>
              )} */}
      {/* </Box>
          </GlassDrawer>
        </GlassAppBar>
      </Box> */}

      <Menu
        anchorEl={notificationsAnchor}
        open={Boolean(notificationsAnchor)}
        onClose={handleNotificationsClose}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: '80vh',
            overflowY: 'auto',
            mt: 1,
            boxShadow: 3,
            borderRadius: 2,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant='h6' sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          <Button
            color='primary'
            size='small'
            sx={{ textTransform: 'none' }}
            onClick={async () => {
              await api.notification.readAllMessagesUpdate();
              fetchNotifications();
            }}
          >
            Mark all as read
          </Button>
        </Box>
        <Divider />
        <List sx={{ p: 0 }}>
          {notifications.map((notification) => (
            <React.Fragment key={notification.id}>
              <ListItem
                alignItems='flex-start'
                sx={{
                  bgcolor: notification.isViewed
                    ? 'transparent'
                    : alpha('#1976d2', 0.04),
                  '&:hover': { bgcolor: alpha('#1976d2', 0.08) },
                  transition: 'background-color 0.2s',
                  cursor: 'pointer',
                }}
                onClick={async () => {
                  if (!notification.isViewed) {
                    await api.notification.viewMessageUpdate({
                      id: notification.id,
                    });
                    setNotifications((prev) =>
                      prev.map((i) =>
                        i.id === notification.id ? { ...i, isViewed: true } : i,
                      ),
                    );
                  }
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <ChatIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography
                      variant='subtitle2'
                      sx={{ fontWeight: notification.isViewed ? 400 : 600 }}
                    >
                      {notification.processType || 'Notification'}
                    </Typography>
                  }
                  secondary={
                    <React.Fragment>
                      <Typography
                        variant='body2'
                        color='text.primary'
                        sx={{ mt: 0.5, mb: 0.5 }}
                      >
                        {notification.message}
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {notification.createdAt
                          ? formatDistanceToNow(
                              new Date(notification.createdAt),
                              { addSuffix: true },
                            )
                          : ''}
                      </Typography>
                    </React.Fragment>
                  }
                />
              </ListItem>
              <Divider component='li' />
            </React.Fragment>
          ))}
        </List>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
          <Button
            color='primary'
            size='small'
            sx={{
              textTransform: 'none',
              fontWeight: 500,
            }}
            onClick={() => {
              handleNotificationsClose();
              navigate('/notifications');
            }}
          >
            View All Notifications
          </Button>
        </Box>
      </Menu>
    </>
  );
};

const mapStateToProps = (state: HandymanAppState): StateFromProps => {
  return {
    authData: state.user.authData,
    currentUserDetails: state.user.currentUserDetails,
  };
};

export default connect<
  StateFromProps,
  DispatchFromProps,
  any,
  HandymanAppState
>(mapStateToProps, {
  logout,
  getCurrentUserDetails,
})(AppBarComponent);
